import { useQuery } from '@apollo/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import type { ColumnDef } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useMemo, useState } from 'react';
import { SortIndicator, useTradingTable } from '../hooks/useTradingTable';
import {
  FETCH_BULK_TRADE_REQUESTS_BY_SOURCE,
  FETCH_BULK_TRADE_RUN_ALLOCATIONS,
  FETCH_BULK_TRADE_RUN_DETAIL,
  FETCH_SUB_TRADE_REQUESTS_FULL,
  FETCH_TRADE_REQUESTS_FOR_BATCH,
} from '../queries';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  BORDER_RIGHT,
  BORDERED_BOX_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
  TRUNCATE_SX,
  VALUE_ROW_SX,
} from '../styles';
import { tradeSideColor } from '../utils';
import BulkTradeRunInlineWorkflow from './bulkTradeRunInlineWorkflow';
import OptimizerInlineWorkflow from './optimizerInlineWorkflow';

const elapsed = (start: string, end?: string) => {
  const diff =
    (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
};

const StatTabBar = ({
  tabs,
  trailing,
}: {
  tabs: {
    label: string;
    value?: string | number;
    content?: React.ReactNode;
    color?: string;
    onClick?: () => void;
    isSelected?: boolean;
    flex?: number | string;
  }[];
  trailing?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0,
      mb: 1.5,
      ...BORDERED_BOX_SX,
      overflow: 'hidden',
      bgcolor: 'background.paper',
    }}
  >
    {tabs.map((stat, idx) => (
      <Box
        key={stat.label}
        onClick={stat.onClick}
        sx={{
          flex: stat.flex ?? 1,
          py: 0.75,
          px: 1.5,
          cursor: stat.onClick ? 'pointer' : 'default',
          textAlign: 'center',
          ...(idx < tabs.length - 1 || trailing ? BORDER_RIGHT : {}),
          bgcolor: stat.isSelected ? 'action.selected' : 'transparent',
          '&:hover': stat.onClick ? { bgcolor: 'action.hover' } : {},
        }}
      >
        <Typography
          variant='labelSmall'
          sx={{ ...SECTION_HEADER_SX, color: stat.color || 'text.secondary' }}
        >
          {stat.label}
        </Typography>
        {stat.content ?? (
          <Typography
            variant='bodyMedium'
            sx={{
              ...BOLD_SX,
              color: stat.color || 'text.primary',
              fontWeight: 500,
            }}
          >
            {stat.value}
          </Typography>
        )}
      </Box>
    ))}
    {trailing}
  </Box>
);

const TradeDetailTable = ({
  columns,
  tradeTable,
  children,
  maxHeight = 360,
  theme,
}: {
  columns: { id: string; label: string; w: string; number?: boolean }[];
  tradeTable: ReturnType<typeof useTradingTable>['table'];
  children: React.ReactNode;
  maxHeight?: number;
  theme: Theme;
}) => (
  <Box sx={{ maxHeight, overflowY: 'auto', ...BORDERED_BOX_SX }}>
    <Table sx={{ bgcolor: theme.color.surface, ...ALIGNED_TABLE_SX }}>
      <TableHead>
        <TableRow sx={{ bgcolor: theme.color.background }}>
          {columns.map((col) => {
            const column = tradeTable.getColumn(col.id);
            return (
              <TableHeadCell
                key={col.id}
                number={col.number}
                onClick={column?.getToggleSortingHandler()}
                sx={{
                  padding: '6px 16px',
                  width: col.w,
                  ...SORTABLE_HEADER_SX,
                }}
              >
                {col.label}
                <SortIndicator sorted={column?.getIsSorted() || false} />
              </TableHeadCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  </Box>
);

type TradeDetailLayout = 'optimizer' | 'bulkTradeRun';

type NormalizedTrade = {
  id: string;
  type: string;
  account: string;
  ticker: string;
  exchange?: string;
  quantity: number;
  price: number;
  totalValue: number;
  currency?: string;
  tradeDate?: string;
  settlementDate?: string;
  isSellAll?: boolean;
  securityName?: string;
  state?: string;
};

const TradeDetailRow = ({
  t,
  layout,
  theme,
}: {
  t: NormalizedTrade;
  layout: TradeDetailLayout;
  theme: Theme;
}) => {
  const isBuy = t.type === 'BUY';
  const cellSx = (extra: object = {}) => ({ padding: '4px 16px', ...extra });
  const dateCell = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');
  if (layout === 'optimizer') {
    return (
      <TableRow sx={{ '&:hover': { bgcolor: theme.color.background } }}>
        <TableCell sx={cellSx({ width: '48px' })}>
          <Typography
            variant='labelSmall'
            sx={{ fontWeight: 800, color: tradeSideColor(theme.color, isBuy) }}
          >
            {t.type}
            {t.isSellAll ? ' ALL' : ''}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx()}>
          <Typography variant='labelSmall' sx={{ ...BOLD_SX }}>
            {t.ticker}
          </Typography>
          {t.exchange && (
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ color: theme.color.onDisabled }}
            >
              {t.exchange}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ ...cellSx(), ...TRUNCATE_SX, maxWidth: 160 }}>
          {t.account}
        </TableCell>
        <TableCell number sx={cellSx()}>
          {t.quantity > 0 ? t.quantity.toFixed(2) : '—'}
        </TableCell>
        <TableCell number sx={cellSx({ color: theme.color.onSurfaceVariant })}>
          {t.price > 0 ? `$${t.price.toFixed(2)}` : '—'}
        </TableCell>
        <TableCell
          number
          sx={cellSx({
            ...VALUE_ROW_SX,
            color: tradeSideColor(theme.color, isBuy),
          })}
        >
          $
          {t.totalValue?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          {t.currency || ''}
        </TableCell>
        <TableCell sx={cellSx({ color: theme.color.onSurfaceVariant })}>
          {dateCell(t.tradeDate)}
        </TableCell>
        <TableCell sx={cellSx({ color: theme.color.onSurfaceVariant })}>
          {dateCell(t.settlementDate)}
        </TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow sx={{ '&:hover': { bgcolor: theme.color.background } }}>
      <TableCell sx={cellSx({ width: '50px' })}>
        <Typography
          variant='labelSmall'
          sx={{ fontWeight: 800, color: tradeSideColor(theme.color, isBuy) }}
        >
          {t.type}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...cellSx(), ...TRUNCATE_SX, maxWidth: 140 }}>
        {t.account}
      </TableCell>
      <TableCell sx={cellSx()}>
        <Typography variant='labelSmall' sx={{ ...BOLD_SX }}>
          {t.ticker}
        </Typography>
      </TableCell>
      <TableCell sx={cellSx({ color: theme.color.onSurfaceVariant })}>
        {t.exchange}
      </TableCell>
      <TableCell number sx={cellSx()}>
        {t.quantity?.toFixed(2)}
      </TableCell>
      <TableCell number sx={cellSx()}>
        ${t.price?.toFixed(2)}
      </TableCell>
      <TableCell number sx={cellSx({ ...VALUE_ROW_SX })}>
        ${t.totalValue?.toFixed(2)} {t.currency === 'CAD' ? 'CA' : 'US'}
      </TableCell>
      <TableCell sx={cellSx({ color: 'text.secondary' })}>
        {dateCell(t.tradeDate)}
      </TableCell>
      <TableCell sx={cellSx({ color: 'text.secondary' })}>
        {dateCell(t.settlementDate)}
      </TableCell>
    </TableRow>
  );
};

export type NormalizedOp = {
  id: string;
  type: 'optimizer' | 'bulkTradeRun';
  state: string;
  label: string;
  progress: number;
  isActive: boolean;
  isComplete: boolean;
  isFailed: boolean;
  createdAt: string;
  updatedAt: string;
  tradeCount: number;
  nextAction: string;
  raw: Record<string, any>;
};

const OperationDetailPanel = ({
  op,
  onRefresh,
  parentPolling = false,
}: {
  op: NormalizedOp;
  onRefresh: () => void;
  parentPolling?: boolean;
}) => {
  const theme = useTheme();
  const [tradeTypeFilter, setTradeTypeFilter] = useState<
    'ALL' | 'BUY' | 'SELL'
  >('ALL');
  const [exchangeFilter, setExchangeFilter] = useState<string>('ALL');

  const { data: detailData } = useQuery(FETCH_BULK_TRADE_RUN_DETAIL, {
    variables: { bulkTradeRunId: op.id },
    skip: op.type !== 'bulkTradeRun',
    fetchPolicy: 'cache-and-network',
  });
  const btrDetail = detailData?.fetchBulkTradeRun?.bulkTradeRun;
  const enrichedRaw = useMemo(
    () => (btrDetail ? { ...op.raw, ...btrDetail } : op.raw),
    [op.raw, btrDetail],
  );

  const { data: allocationsData, loading: loadingAllocations } = useQuery(
    FETCH_BULK_TRADE_RUN_ALLOCATIONS,
    {
      variables: {
        input: {
          filter: { bulkTradeRunId: op.id },
          pagination: { perPage: 500 },
        },
      },
      skip: op.type === 'optimizer' || op.isActive,
      fetchPolicy: 'cache-first',
    },
  );

  const { data: tradeRequestsData, loading: loadingTradeRequests } = useQuery(
    FETCH_TRADE_REQUESTS_FOR_BATCH,
    {
      variables: {
        input: {
          filter: { bulkTradeRunId: op.id },
          pagination: { perPage: 500 },
        },
      },
      skip: op.type === 'optimizer' || op.isActive,
      fetchPolicy: 'cache-first',
    },
  );

  const { data: bulkTradeReqData, loading: loadingBulkTradeReq } = useQuery(
    FETCH_BULK_TRADE_REQUESTS_BY_SOURCE,
    {
      variables: {
        input: { filter: { source: 'PORTFOLIO_OPTIMIZER', sourceId: op.id } },
      },
      skip: op.type !== 'optimizer',
      fetchPolicy: 'cache-first',
    },
  );
  const optimizerBulkTradeRequestId =
    bulkTradeReqData?.fetchBulkTradeRequests?.bulkTradeRequests?.[0]?.id;

  const { data: optimizerSubTradesData, loading: loadingOptimizerSubTrades } =
    useQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
      variables: {
        input: {
          filter: { bulkTradeRequestId: optimizerBulkTradeRequestId },
          pagination: { perPage: 500, offSet: 0 },
        },
      },
      skip: op.type !== 'optimizer' || !optimizerBulkTradeRequestId,
      fetchPolicy: 'cache-first',
    });
  const loadingOptimizerTrades =
    loadingBulkTradeReq || loadingOptimizerSubTrades;

  // Normalize trade data — works for both optimizers and bulk trade runs
  const normalizedTrades = useMemo(() => {
    // Bulk trade run: allocations first, then trade requests
    if (op.type === 'bulkTradeRun') {
      const allocations =
        allocationsData?.fetchBulkTradeRunAllocations
          ?.bulkTradeRunAllocations || [];
      const tradeRequests =
        tradeRequestsData?.fetchTradeRequests?.tradeRequests || [];
      if (allocations.length > 0) {
        return allocations.map((a: any) => ({
          id: a.id,
          type: a.side,
          account: a.account,
          ticker: a.symbol,
          exchange: a.market,
          quantity: a.shareQuantity,
          price: a.fillPrice / 100,
          totalValue: (a.shareQuantity * a.fillPrice) / 100,
          currency: a.tradeCurrency,
          tradeDate: a.tradeDate,
          settlementDate: a.settlementDate,
        }));
      }
      if (tradeRequests.length > 0) {
        return tradeRequests.map((t: any) => ({
          id: t.id,
          type: t.type,
          account:
            `${t.account?.user?.firstName || ''} ${t.account?.user?.lastName || ''}`.trim(),
          ticker: t.financialProduct?.ticker || 'N/A',
          exchange: t.financialProduct?.exchange || '?',
          quantity: t.actualQuantity || t.expectedQuantity || 0,
          price:
            (t.actualTradePriceCents || t.expectedTradePriceCents || 0) / 100,
          totalValue:
            ((t.actualQuantity || t.expectedQuantity || 0) *
              (t.actualTradePriceCents || t.expectedTradePriceCents || 0)) /
            100,
          currency: t.currency,
          tradeDate: t.createdAt,
          settlementDate: t.settlementDate,
        }));
      }
      return [];
    }

    // Optimizer: sub-trade requests (via BulkTradeRequest -> bulkTradeRequestId)
    const subTrades =
      optimizerSubTradesData?.fetchSubTradeRequests?.subTradeRequests || [];
    return subTrades.map((st: any) => {
      const qty =
        st.actualQuantity ||
        st.expectedQuantity ||
        (st.moneyAllocatedCents
          ? Math.abs(st.moneyAllocatedCents) /
            (st.financialProduct?.currentPriceCents || 1)
          : 0);
      const price =
        st.actualTradePriceCents ||
        st.expectedTradePriceCents ||
        st.financialProduct?.currentPriceCents ||
        0;
      const fullName = [
        st.subAccount?.account?.user?.firstName,
        st.subAccount?.account?.user?.lastName,
      ]
        .filter(Boolean)
        .join(' ');
      return {
        id: st.id,
        type: st.type,
        account: fullName
          ? `${fullName} · ${st.subAccount?.account?.type || ''}`
          : st.subAccount?.account?.type || '—',
        ticker: st.financialProduct?.ticker || 'N/A',
        securityName: st.financialProduct?.translatedName?.en || '',
        exchange: st.financialProduct?.exchange || '',
        quantity: qty,
        price: price / 100,
        totalValue: Math.abs(st.moneyAllocatedCents || 0) / 100,
        currency: st.currency || '',
        state: st.state,
        tradeDate: st.createdAt || '',
        settlementDate: st.settlementDate || '',
        isSellAll: st.isSellAll,
      };
    });
  }, [op.type, allocationsData, tradeRequestsData, optimizerSubTradesData]);

  const filteredTrades = useMemo(
    () =>
      normalizedTrades.filter(
        (t: any) =>
          (tradeTypeFilter === 'ALL' || t.type === tradeTypeFilter) &&
          (exchangeFilter === 'ALL' || t.exchange === exchangeFilter),
      ),
    [normalizedTrades, tradeTypeFilter, exchangeFilter],
  );

  const tradeColumns: ColumnDef<any>[] = useMemo(
    () => [
      { id: 'type', header: 'Type', accessorFn: (r: any) => r.type },
      { id: 'account', header: 'Account', accessorFn: (r: any) => r.account },
      { id: 'ticker', header: 'Security', accessorFn: (r: any) => r.ticker },
      { id: 'exchange', header: 'Exch', accessorFn: (r: any) => r.exchange },
      { id: 'quantity', header: 'Qty', accessorFn: (r: any) => r.quantity },
      { id: 'price', header: 'Price', accessorFn: (r: any) => r.price },
      { id: 'total', header: 'Total', accessorFn: (r: any) => r.totalValue },
      {
        id: 'tradeDate',
        header: 'Trade',
        accessorFn: (r: any) => r.tradeDate || '',
      },
      {
        id: 'settleDate',
        header: 'Settle',
        accessorFn: (r: any) => r.settlementDate || '',
      },
    ],
    [],
  );

  const { table: tradeTable } = useTradingTable({
    data: filteredTrades,
    columns: tradeColumns,
  });

  const buys = normalizedTrades.filter((t: any) => t.type === 'BUY').length;
  const sells = normalizedTrades.filter((t: any) => t.type === 'SELL').length;
  const exchanges: string[] = Array.from(
    new Set(normalizedTrades.map((t: any) => t.exchange as string)),
  );

  return (
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
        <Box sx={{ py: 1.5, px: 2, bgcolor: 'action.hover' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {[
              {
                label: 'Created',
                value: new Date(op.createdAt).toLocaleString(),
              },
              ...(op.updatedAt
                ? [
                    {
                      label: op.isComplete ? 'Completed' : 'Updated',
                      value: new Date(op.updatedAt).toLocaleString(),
                    },
                  ]
                : []),
              {
                label: 'Duration',
                value: elapsed(
                  op.createdAt,
                  op.isComplete || op.isFailed ? op.updatedAt : undefined,
                ),
              },
              { label: 'State', value: op.state.replace(/_/g, ' ') },
              ...(op.raw.organization?.name
                ? [{ label: 'Organization', value: op.raw.organization.name }]
                : []),
              ...(op.type === 'optimizer'
                ? [
                    ...(op.raw.type
                      ? [{ label: 'Type', value: op.raw.type }]
                      : []),
                    ...(op.raw.numPortfolios
                      ? [
                          {
                            label: 'Portfolios',
                            value: `${op.raw.numPortfolios}`,
                          },
                        ]
                      : []),
                    ...(op.raw.subTradeRequestsCreated != null
                      ? [
                          {
                            label: 'Trades Created',
                            value: `${op.raw.subTradeRequestsCreated}`,
                          },
                        ]
                      : []),
                    ...(op.raw.expectedTotalSubTradeRequests
                      ? [
                          {
                            label: 'Expected Trades',
                            value: `${op.raw.expectedTotalSubTradeRequests}`,
                          },
                        ]
                      : []),
                  ]
                : []),
            ].map((item) => (
              <Box key={item.label}>
                <Typography
                  variant='labelSmall'
                  component='div'
                  sx={{ ...SECTION_HEADER_SX }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant='labelSmall'
                  component='div'
                  sx={VALUE_ROW_SX}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {op.type === 'bulkTradeRun' && op.isActive && (
            <BulkTradeRunInlineWorkflow
              operation={enrichedRaw}
              onRefresh={onRefresh}
              parentPolling={parentPolling}
            />
          )}

          {op.type === 'optimizer' && (
            <OptimizerInlineWorkflow
              operation={op.raw}
              onRefresh={onRefresh}
              trades={normalizedTrades}
              loadingTrades={loadingOptimizerTrades}
              parentPolling={parentPolling}
            />
          )}

          {op.type === 'bulkTradeRun' && op.isComplete && !op.isFailed && (
            <>
              <StatTabBar
                tabs={[
                  {
                    label: 'Total',
                    value:
                      loadingAllocations || loadingTradeRequests
                        ? '...'
                        : `${normalizedTrades.length}`,
                    onClick: () => {
                      setTradeTypeFilter('ALL');
                      setExchangeFilter('ALL');
                    },
                    isSelected:
                      tradeTypeFilter === 'ALL' && exchangeFilter === 'ALL',
                  },
                  ...(buys > 0
                    ? [
                        {
                          label: 'Buys',
                          value:
                            loadingAllocations || loadingTradeRequests
                              ? '...'
                              : `${buys}`,
                          color: theme.color.positive,
                          onClick: () => {
                            setTradeTypeFilter('BUY');
                            setExchangeFilter('ALL');
                          },
                          isSelected: tradeTypeFilter === 'BUY',
                        },
                      ]
                    : []),
                  ...(sells > 0
                    ? [
                        {
                          label: 'Sells',
                          value:
                            loadingAllocations || loadingTradeRequests
                              ? '...'
                              : `${sells}`,
                          color: theme.color.negative,
                          onClick: () => {
                            setTradeTypeFilter('SELL');
                            setExchangeFilter('ALL');
                          },
                          isSelected: tradeTypeFilter === 'SELL',
                        },
                      ]
                    : []),
                  ...exchanges.map((ex) => ({
                    label: ex,
                    value:
                      loadingAllocations || loadingTradeRequests
                        ? '...'
                        : `${normalizedTrades.filter((t: any) => t.exchange === ex).length}`,
                    onClick: () => {
                      setExchangeFilter(ex);
                      setTradeTypeFilter('ALL');
                    },
                    isSelected: exchangeFilter === ex,
                  })),
                ]}
              />

              {loadingAllocations || loadingTradeRequests ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size='medium' />
                </Box>
              ) : (
                normalizedTrades.length > 0 && (
                  <TradeDetailTable
                    theme={theme}
                    columns={[
                      { id: 'type', label: 'Type', w: '50px' },
                      { id: 'account', label: 'Account', w: 'auto' },
                      { id: 'ticker', label: 'Security', w: '70px' },
                      { id: 'exchange', label: 'Exch', w: '50px' },
                      { id: 'quantity', label: 'Qty', w: '70px', number: true },
                      { id: 'price', label: 'Price', w: '70px', number: true },
                      { id: 'total', label: 'Total', w: '90px', number: true },
                      { id: 'tradeDate', label: 'Trade', w: '75px' },
                      { id: 'settleDate', label: 'Settle', w: '75px' },
                    ]}
                    tradeTable={tradeTable}
                    maxHeight={320}
                  >
                    {tradeTable.getRowModel().rows.map((row) => (
                      <TradeDetailRow
                        key={row.original.id}
                        t={row.original}
                        layout='bulkTradeRun'
                        theme={theme}
                      />
                    ))}
                  </TradeDetailTable>
                )
              )}

              {(enrichedRaw.buyBulkTradeFileUrl ||
                enrichedRaw.sellBulkTradeFileUrl ||
                enrichedRaw.mutualFundFileUrl) && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{ ...SECTION_HEADER_SX, mr: 0.5, alignSelf: 'center' }}
                  >
                    Files:
                  </Typography>
                  {[
                    {
                      label: 'Buy Orders',
                      url: enrichedRaw.buyBulkTradeFileUrl,
                    },
                    {
                      label: 'Sell Orders',
                      url: enrichedRaw.sellBulkTradeFileUrl,
                    },
                    {
                      label: 'Mutual Funds',
                      url: enrichedRaw.mutualFundFileUrl,
                    },
                  ]
                    .filter((f) => f.url)
                    .map((f) => (
                      <Button
                        key={f.label}
                        size='small'
                        variant='text'
                        onClick={() => window.open(f.url, '_blank')}
                      >
                        {f.label}
                      </Button>
                    ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default OperationDetailPanel;
